// src/components/PaymentLogsModal.tsx

import React from "react";
import { X, CalendarDays, ReceiptText, Banknote, BadgePercent, MessageSquareText } from "lucide-react";
import { Payment } from "../types/payment";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  payments: Payment[];
  studentName: string;
}

export default function PaymentLogsModal({
  isOpen,
  onClose,
  payments,
  studentName,
}: Props) {
  if (!isOpen) return null;

  const totalAmount = payments.reduce(
    (sum, payment) => sum + payment.amount,
    0,
  );
  const totalDiscount = payments.reduce(
    (sum, payment) => sum + (payment.discountAmount || 0),
    0,
  );

  return (
    <div
      className="
                fixed inset-0 z-[1600]
                flex items-center justify-center
                bg-slate-950/45 px-4 py-6
                backdrop-blur-[2px]
            "
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="payment-logs-title"
        className="
                    flex max-h-[88vh] w-full max-w-[620px]
                    flex-col overflow-hidden
                    rounded-[18px]
                    border border-slate-200
                    bg-white
                    shadow-[0_24px_70px_rgba(15,23,42,0.22)]
                "
      >
        {/* Header */}
        <div
          className="
                        flex shrink-0 items-start justify-between gap-4
                        border-b border-slate-100
                        px-5 py-4
                    "
        >
          <div className="min-w-0">
            <p
              className="
                                m-0 text-[10px] font-bold uppercase
                                tracking-[0.12em] text-blue-600
                            "
            >
              Payment &amp; discount history
            </p>

            <h2
              id="payment-logs-title"
              className="
                                mb-0 mt-1 truncate
                                text-[18px] font-bold text-slate-950
                            "
            >
              Payments for {studentName}
            </h2>

            <p className="mb-0 mt-1 text-[12px] text-slate-500">
              {payments.length} financial entr{payments.length === 1 ? "y" : "ies"} recorded
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close payment history"
            title="Close"
            className="
                            inline-flex h-9 w-9 shrink-0 items-center justify-center
                            rounded-[9px] border border-slate-200 bg-white
                            text-slate-500 transition
                            hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900
                            focus:outline-none focus:ring-2 focus:ring-slate-200
                        "
          >
            <X size={17} />
          </button>
        </div>

        {/* Summary */}
        <div className="shrink-0 border-b border-slate-100 bg-slate-50/60 px-5 py-3">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-5">
            <div className="flex items-center justify-between gap-3">
              <span className="text-[12px] font-medium text-slate-500">Total received</span>
              <span className="text-[15px] font-bold text-emerald-700">
                Rs {totalAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-[12px] font-medium text-slate-500">Total discount</span>
              <span className="text-[15px] font-bold text-blue-700">
                Rs {totalDiscount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </div>

        {/* Payment list */}
        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
          {payments.length > 0 ? (
            <div className="space-y-3">
              {payments.map((payment) => (
                <article
                  key={payment.id}
                  className="
    overflow-hidden
    rounded-[14px]
    border border-slate-200
    bg-white
    shadow-md
    transition
    hover:border-slate-300
    hover:shadow-lg
  "
                >
                  <div className="divide-y divide-slate-100 px-4">
                    <div className="flex items-center justify-between gap-4 py-3">
                      <span className="inline-flex items-center gap-2 text-[13px] font-medium text-slate-500">
                        <CalendarDays
                          size={15}
                          className="shrink-0 text-slate-400"
                        />
                        Date
                      </span>

                      <span className="text-right text-[14px] font-semibold text-slate-800">
                        {payment.paymentDate.slice(0, 10)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between gap-4 py-3">
                      <span className="inline-flex items-center gap-2 text-[13px] font-medium text-slate-500">
                        <ReceiptText
                          size={15}
                          className="shrink-0 text-slate-400"
                        />
                        Receipt
                      </span>

                      <span
                        className="
          max-w-[65%]
          truncate
          text-right
          text-[14px]
          font-semibold
          text-slate-800
        "
                        title={payment.receiptNumber}
                      >
                        {payment.receiptNumber || "—"}
                      </span>
                    </div>

                    <div className="flex items-center justify-between gap-4 py-3">
                      <span className="inline-flex items-center gap-2 text-[13px] font-medium text-slate-500">
                        <Banknote
                          size={15}
                          className="shrink-0 text-slate-400"
                        />
                        Amount
                      </span>

                      <span className="text-right text-[15px] font-bold text-emerald-700">
                        Rs {payment.amount.toFixed(2)}
                      </span>
                    </div>

                    {(payment.discountAmount || 0) > 0 && (
                      <>
                        <div className="flex items-center justify-between gap-4 py-3">
                          <span className="inline-flex items-center gap-2 text-[13px] font-medium text-slate-500">
                            <BadgePercent size={15} className="shrink-0 text-slate-400" />
                            Discount
                          </span>
                          <span className="text-right text-[15px] font-bold text-blue-700">
                            Rs {(payment.discountAmount || 0).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex items-start justify-between gap-4 py-3">
                          <span className="inline-flex shrink-0 items-center gap-2 text-[13px] font-medium text-slate-500">
                            <MessageSquareText size={15} className="shrink-0 text-slate-400" />
                            Discount reason
                          </span>
                          <span className="max-w-[65%] whitespace-pre-wrap break-words text-right text-[13px] font-medium text-slate-800">
                            {payment.discountReason || "—"}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div
              className="
                                flex min-h-[220px] flex-col
                                items-center justify-center text-center
                            "
            >
              <div
                className="
                                    grid h-11 w-11 place-items-center
                                    rounded-xl bg-slate-100 text-slate-400
                                "
              >
                <ReceiptText size={20} />
              </div>

              <h3 className="mb-0 mt-3 text-[13px] font-semibold text-slate-900">
                No payment records
              </h3>

              <p className="mb-0 mt-1 text-[11px] text-slate-500">
                No payments are available for this student.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="
                        flex shrink-0 items-center justify-end
                        border-t border-slate-100
                        bg-slate-50/50 px-5 py-3
                    "
        >
          <button
            type="button"
            onClick={onClose}
            className="
                            inline-flex h-9 items-center justify-center
                            rounded-[9px] border border-slate-200 bg-white
                            px-4 text-[12px] font-semibold text-slate-700
                            transition
                            hover:border-slate-300 hover:bg-slate-50 hover:text-slate-950
                            focus:outline-none focus:ring-2 focus:ring-slate-200
                        "
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
